import { useSyncExternalStore } from 'react';
import { paintThemeColor } from './appearance';

export type Theme = 'light' | 'dark' | 'system';
const KEY = 'deeds.theme';
const listeners = new Set<() => void>();

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  paintThemeColor();
}

export function initTheme(): void {
  apply(read());
  // Following the system: the browser's bar follows it too.
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', paintThemeColor);
}

export function setTheme(theme: Theme): void {
  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    // Storage may be unavailable (private mode); the attribute still applies for this page.
  }
  apply(theme);
  for (const l of listeners) l();
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
    () => 'system' as Theme,
  );
  return [theme, setTheme];
}

/** What the OS is asking for right now, for the "Match the system" hint. */
export function systemTheme(): 'light' | 'dark' {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
